import React, {useCallback, useEffect} from "react";
import {CategoryModel} from "../../../models/category.model";
import {useSelector} from "react-redux";
import {RootState} from "../../../store/root-reducer";
import {makeStyles} from "@material-ui/core/styles";
import {StyledTreeItem, StyledTreeMinusSquare, StyledTreePlusSquare} from "../../shared/styled-tree";
import {StyledCheckbox} from "../../form/checkbox";
import TreeView from "@material-ui/lab/TreeView";
import {useFormik} from "formik";
import {Grid} from "@material-ui/core";
import {StyledButton} from "../../ui-base/button";
import styled from "styled-components";
import {StyledInput} from "../../form/input";
import {useTranslation} from "react-i18next";
import {CategoryCsvModel} from "../../../models/category-csv.model";

const useStyles = makeStyles({
    root: {},
    checkbox: {
        paddingTop: 9,
        paddingBottom: 9,
        paddingRight: 5,
        paddingLeft: 5,
        marginLeft: -3
    }
});

export const CategoriesPicker: React.FC<{handleSelectedCategory: Function, initProfile?: {}}> = ({handleSelectedCategory, initProfile}) => {
    const [selected, setSelected] = React.useState<CategoryModel[]>([]);
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [tree, setTree] = React.useState<CategoryModel[]>([]);
    const [origTree, setOrigTree] = React.useState<CategoryModel[]>([]);
    const [initProfileSet, setInitProfileSet] = React.useState<boolean>(false);
    const { t } = useTranslation(['publisher', 'common']);
    const mainCategory = 'mainCategory8';

    const themaCategories: CategoryModel[] = useSelector(
        (state: RootState) => state.categories.categories
    );

    const categoriesCsv: CategoryCsvModel[] = useSelector(
        (state: RootState) => state.categories.categoriesCsv
    );

    const classes = useStyles();

    const getCategoriesCodeByMainCategory = useCallback(() => {
        return categoriesCsv.filter((category) => category[mainCategory] === 'TRUE' && category['Do wyrzucenia'] === 'FALSE').map((category) => category.Code);
    }, [categoriesCsv, mainCategory]);

    useEffect(() => {
        if (themaCategories && themaCategories.length > 0) {
            const tmpThemaCategories = JSON.parse(JSON.stringify(themaCategories));

            const codes: string[] = getCategoriesCodeByMainCategory();
            const filteredCategories = tmpThemaCategories.filter((category) => codes.includes(category.code));
            setOrigTree([]);
            setTree([]);
            setExpanded([]);

            const categoriesReady = filteredCategories.filter((category) => {
                return !category.parent_code;
            }).map(category => {
                setChildren(category);
                return category;
            });

            const categoryWithParent = filteredCategories.filter((category) => category.parent_code && category.parent_code.length === 1);

            categoryWithParent.forEach((category: CategoryModel) => {
                let parent: CategoryModel = categoriesReady.find((res) => res.code === category.parent_code);

                if (!parent) {
                    const newCategory = tmpThemaCategories.find((themaCat) => themaCat.code === category.parent_code);
                    setChildren(newCategory);
                    categoriesReady.push(newCategory);
                    parent = newCategory;
                }

                setChildren(parent);

                if (parent) {
                    if (parent.children.indexOf(category) === -1) {
                        parent.children.push(category);
                    }
                }
            });

            setOrigTree(categoriesReady);
            setTree(categoriesReady);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (initProfile && tree?.length > 0 && !initProfileSet) {
            setInitProfileSet(true);
            const copyTree = JSON.parse(JSON.stringify(tree));
            const total = initProfile['TOTAL'];
            const keys = Object.keys(initProfile);
            const selected = [];

            //set only parent
            keys.filter((key) => key.length === 1).forEach((key) => {
                const category = copyTree.find((mainCategory) => mainCategory.code === key);
                category.count = Math.round((((100 * initProfile[key]) / total) + Number.EPSILON) * 100) / 100;
                selected.push(category);
            });

            //set only children
            keys.filter((key) => key.length === 2).forEach((key) => {
                const mainCategory = copyTree.find((mainCategory) => mainCategory.code === key[0]);
                const childCategory = mainCategory.children.find((childCat) => childCat.code === key);
                childCategory.count = Math.round((((100 * initProfile[key]) / initProfile[key[0]]) + Number.EPSILON) * 100) / 100;
                selected.push(childCategory);
            });

            setTree(copyTree)
            setSelected(selected);
            handleSelectedCategory(selected);
        }
        // eslint-disable-next-line
    }, [initProfile, tree]);

    const setChildren = (parent: CategoryModel) => {
        if (parent && !parent.children) {
            parent.children = [];
        }
    }

    const getOnChange = (checked: boolean, node: CategoryModel) => {
        let array;

        if (checked) {
            array = [...selected, node];
            if (node.parent_code) {
                const parent = selected.find(value => value.code === node.parent_code);
                if (!parent) {
                    array = [...array, tree.find(value => value.code === node.parent_code)];
                }
            }
        } else {
            if (!node.parent_code) {
                array = selected.filter(value => value.code !== node.code && value.parent_code !== node.code);
            } else {
                array = selected.filter(value => value.code !== node.code);
            }
        }

        setSelected(array);
        handleSelectedCategory(array);
    }

    const getTreeItemsFromData = (treeItems: CategoryModel[]) => {
        return treeItems.map(treeItemData => {
            let children = undefined;
            if (treeItemData.children && treeItemData.children.length > 0) {
                children = getTreeItemsFromData(treeItemData.children);
            }
            return (
                <StyledTreeItem
                    key={treeItemData.code}
                    // @ts-ignore
                    nodeId={treeItemData.code}
                    label={
                        <>
                            <StyledCheckbox
                                id={''}
                                name={''}
                                onChange={event =>
                                    getOnChange(event.currentTarget.checked, treeItemData)
                                }
                                value={selected.some(item => item.code === treeItemData.code)}
                                label={treeItemData.name}
                                onClick={e => {e.stopPropagation();}}
                                className={classes.checkbox}
                                key={treeItemData.code}
                            />
                        </>
                    }
                    children={children}/>
            );
        });
    };

    const DataTreeView = () => {
        return (
            <TreeView
                defaultCollapseIcon={<StyledTreeMinusSquare/>}
                defaultExpandIcon={<StyledTreePlusSquare/>}
                expanded={expanded}
                onNodeToggle={handleToggle}
            >
                {getTreeItemsFromData(tree)}
            </TreeView>
        );
    };

    const dfs = (node: CategoryModel, term, foundIDS) => {
        let isMatching = node.name && node.name.toLowerCase().indexOf(term.toLowerCase()) > -1;

        if (Array.isArray(node.children)) {
            node.children.forEach((child) => {
                const hasMatchingChild = dfs(child, term, foundIDS);
                isMatching = isMatching || hasMatchingChild;
            });
        }

        if (isMatching && node.code) {
            foundIDS.push(node.code);
        }

        return isMatching;
    }

    const filter = (data, matchedIDS) => {
        return data
            .filter((item) => matchedIDS.indexOf(item.code) > -1)
            .map((item) => ({
                ...item,
                children: item.children ? filter(item.children, matchedIDS) : [],
            }));
    }

    const search = (term, returnIds = false) => {
        if (term) {
            const dataNode = {
                children: origTree,
            };

            const matchedIDS = [];
            dfs(dataNode as CategoryModel, term, matchedIDS);

            setExpanded(matchedIDS);

            if (!returnIds) {
                return filter(origTree, matchedIDS);
            }

            return matchedIDS;
        } else {
            setExpanded([]);
            return [...origTree];
        }

    }

    const searchForm = useFormik({
        initialValues: {
            search: ''
        },
        onSubmit: async values => {
            setTree(search(values.search.toLowerCase()));
        }
    })

    const handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    return (
        <>
            <Grid container direction={'row'} alignItems={"flex-end"} spacing={1}>
                <Grid item xs={true}>
                    <StyledInput
                        id="search"
                        label={t('createPublisherProfile.searchCategoryLabel')}
                        onChange={searchForm.handleChange}
                        value={searchForm.values.search}
                    />
                </Grid>

                <Grid item xs={false} style={{marginBottom: '2px'}}>
                    <StyledButton type="button" color={"violet"} onClick={() => searchForm.submitForm()}>
                        {t('common:search')}
                    </StyledButton>
                </Grid>
            </Grid>

            <StyledTreeWrapper style={{marginTop: '10px'}}>{DataTreeView()}</StyledTreeWrapper>
        </>
    );
}

const StyledTreeLabelWithoutCheckbox = styled('div')`
  padding: 9px 0;
`;

const StyledTreeWrapper = styled('div')`
  margin-top: 50px;
  max-height: 400px;
  overflow-y: auto;
`;
